import * as _ from 'underscore';
import { Formatters, Messages } from '../../codegen/Localize';
import { LockStatusHelper, LockUnlockPermissionsState } from '../helpers/LockStatusHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { L10n } from '../L10n/L10n';
import { ProjectContentType } from '../models/ContentTypes';
import { IProjectInfo } from '../models/IProjectInfo';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IResult } from '../ServerApi/types/IResponse';
import { ConfirmActionDialog } from '../services/ConfirmActionDialog';
import { SetProjectControlledPermissionsAction } from '../services/SetProjectControlledPermissionsAction';

export interface ILockUnlockProjectPermissionsScope extends ng.IScope {
  showConfirmationDialog: () => void;
  authorizables: Set<any>;
  authorizableType: string;
  topLevelProject: INamedItemInfo;

  editable: boolean;
  editabilityShortMessage: string;
  editabilityLongMessage: string;

  projectLockState: {
    newStates: LockUnlockPermissionsState[];
  };

  permissionsDialogMessage: string;
  warningMessage: string;

  canLockProjectPermissions: () => boolean;

  // making this of type LockUnlockProjectPermissionsState doesn't work because then
  // it can only be assigned enum values, and not the enum itself. Thus, it has to be
  // of type any.
  permissionState: any;

  lockState: LockUnlockPermissionsState;

  percentComplete: number;
  busyUnlocking: boolean;

  getMessageIconClass: () => string;

  willLockProjects: () => boolean;

  getLockMessage: () => string;
  getDisabledButtonTooltip: () => string;
}

Module.instance().directive('tbLockUnlockProjectPermissions', [
  '$translate',
  'ConfirmActionDialog',
  'SetProjectControlledPermissionsAction',
  ($translate: ng.translate.ITranslateService,
    confirmActionDialog: ConfirmActionDialog,
    SetProjectControlledPermissionsAction: SetProjectControlledPermissionsAction) => {
    return {
      restrict: 'E',
      replace: true,
      templateUrl: 'lockUnlockProjectPermissions.html',
      scope: {
        authorizableType: '=',
        authorizables: '=',
        editable: '=',
        editabilityShortMessage: '=',
        editabilityLongMessage: '=',
        lockState: '=',
        topLevelProject: '='
      },
      link: (scope: ILockUnlockProjectPermissionsScope) => {
        const projects: Set<IProjectInfo> = getProjects();

        const isProject = () => scope.authorizableType === ProjectContentType.name;
        const authorizablesIncludeChildProject = () => isProject() && projects.any(p => !p.topLevelProject);

        scope.canLockProjectPermissions = () => isProject() && !authorizablesIncludeChildProject();

        scope.willLockProjects = () => {
          return scope.lockState !== LockUnlockPermissionsState.Locked &&
            LockStatusHelper.getCombinedStatusOfAllItems(scope.projectLockState.newStates) ===
            LockUnlockPermissionsState.Locked;
        };

        scope.getMessageIconClass = () => LockStatusHelper.getLockStateMessageIconClass(scope.lockState);

        scope.getLockMessage = () => $translate.instant(LockStatusHelper.getLockMessage(scope.lockState));

        scope.getDisabledButtonTooltip = () => {
          let tooltip: string;
          if (scope.canLockProjectPermissions()) {
            tooltip = '';
          } else if (scope.topLevelProject) {
            tooltip = Messages.lockProjectPermissions_disabledTooltip_withTopLevelProject({
              'TOP_LEVEL_PROJECT_NAME': scope.topLevelProject.name
            });
          } else {
            tooltip = Messages.lockProjectPermissions_disabledTooltip();
          }

          return tooltip;
        };

        // set the default value to 0 so it's not undefined.
        scope.percentComplete = 0;

        scope.permissionState = LockUnlockPermissionsState;

        scope.permissionsDialogMessage = $translate.instant('lockProjectPermissions_dialogMessage',
          {
            PROJECT_COUNT: projects.size(),
            PROJECT_NAME: L10n.elideIfLong(projects.first().name)
          }, L10n.Interpolations.Count);

        scope.warningMessage = Messages.lockProjectPermissionsWarningMessage({
          PROJECT_COUNT: projects.size(),
          PROJECT_COUNT_LOC: Formatters.formatCount(projects.size())});

        scope.showConfirmationDialog = () => {
          scope.projectLockState = { newStates: LockStatusHelper.getLockStatusOfAllContainers(projects) };
          confirmActionDialog.open({
            title: $translate.instant('lockProjectPermissions_dialogTitle'),
            bodyTemplateUrl: 'lockUnlockProjectPermissionsDialog.html',
            scope: scope,
            position: { reserveHeight: 450 },
            actionName: $translate.instant('save'),
            action: () => {
              // TODO: filter through the projects and only select the ones to update whose
              // controlledPermissionsEnabled field is the opposite of the chosen permission source.
              // This reduces the number of projects we have to update and is particularly useful in the case
              // that there are mixed projects - i.e. some projects have permissions locked to the project and others
              // managed by the owner. Additionally, it helps if the user selects the current state of all the projects
              // since that means we don't have to update anything.
              return SetProjectControlledPermissionsAction.execute(projects, scope.projectLockState.newStates, scope)
                .then((result: IResult) => {
                  if (!ResultHelper.hasErrors(result)) {
                    updateControlledPermissionsEnabled(scope.projectLockState.newStates);
                    scope.lockState = LockStatusHelper.getLockState(projects);
                  }
                });
              // TODO: handle error, message $translate.instant("lockProjectPermissionsError")
            },
            confirmDisabled: () => {
              const enumState: LockUnlockPermissionsState =
                LockStatusHelper.getCombinedStatusOfAllItems(scope.projectLockState.newStates);
              return enumState === LockUnlockPermissionsState.Mixed || enumState === scope.lockState;
            }
          });
        };

        function updateControlledPermissionsEnabled(updatedPermissionSource: LockUnlockPermissionsState[]) {
          // TODO: ideally you want to look at the server response to see which projects had their permissions
          // updated and which didn't, but for now this will suffice since the server response is empty for now
          // and not implemented.
          const updatedControlledPermissions: boolean =
            _.first(updatedPermissionSource) === LockUnlockPermissionsState.Locked;

          scope.authorizables.each((proj: any) => {
            if (proj.controlledPermissionsEnabled !== updatedControlledPermissions) {
              proj.controlledPermissionsEnabled = updatedControlledPermissions;
            }
          });
        }

        function getProjects(): Set<IProjectInfo> {
          // TODO: Request information from the server about THE selected project since when we're in the permissions
          // subplace, the counts related to the selected project don't show up, but when you're in the permissions
          // dialog they show up.
          return <Set<IProjectInfo>>scope.authorizables;
        }
      }
    };
  }
]);
